import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "AcademicPost92CheckReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    academic_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        verification_data: false,
        response_data: false,
        subjects: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  mounted() {
    this.position_disclaimer();
  },
  updated() {
    this.position_disclaimer();
  },
  computed: {
    safe_academic_data() {
      return this.academic_data || {};
    },

    result_data() {
      return this.safe_academic_data.result || {};
    },

    verification_data() {
      return this.result_data.verification_data || {};
    },

    response_data() {
      return this.result_data.response_data || {};
    },

    // Reference Numbers
    client_reference_number() {
      return this.result_data.client_reference_number || "N/A";
    },

    reference_number() {
      return this.result_data.reference_number || "N/A";
    },

    batch_number() {
      return this.response_data.batch_nr || "N/A";
    },

    sequence_number() {
      return this.response_data.seq_nr || "N/A";
    },

    // Personal Information from Verification Data
    names() {
      return this.verification_data.names || "N/A";
    },

    surname() {
      return this.verification_data.surname || "N/A";
    },

    full_name() {
      return `${this.names} ${this.surname}`;
    },

    maiden_name() {
      return this.verification_data.maiden_name || this.response_data.maiden_name || "N/A";
    },

    id_number() {
      return this.verification_data.id_number || "N/A";
    },

    date_of_birth() {
      return this.verification_data.date_of_birth || "N/A";
    },

    formatted_date_of_birth() {
      return this.formatDateOfBirth(this.date_of_birth);
    },

    // Academic Information
    year() {
      return this.verification_data.year || "N/A";
    },

    qualification() {
      return this.verification_data.qualification || "N/A";
    },

    qualification_description() {
      return this.getQualificationDescription(this.qualification);
    },

    document_number() {
      return this.verification_data.document_number || this.response_data.doc_nr || "N/A";
    },

    document_type() {
      return this.response_data.doc_type || "N/A";
    },

    aggregate() {
      const agg = this.verification_data.aggregate || this.response_data.agg_adm;
      return agg && agg !== "None" ? agg : "N/A";
    },

    // Subjects Information
    subjects() {
      return this.verification_data.subjects || this.response_data.subjects || [];
    },

    number_of_subjects() {
      return this.response_data.no_subj || this.subjects.length || 0;
    },

    has_subjects() {
      return this.subjects.length > 0;
    },

    // Verification Result
    result_code() {
      return this.response_data.result || "0";
    },

    result_description() {
      return this.response_data.result_description || this.getResultDescription(this.result_code);
    },

    verification_status() {
      return this.safe_academic_data.status || "unknown";
    },

    is_positive_result() {
      // Result code '1' or '4' indicates a confirmed (formerly positive) verification
      const normalizedDescription = this.result_description.toLowerCase();
      return (
        this.result_code === "1" ||
        this.result_code === "4" ||
        normalizedDescription === "confirmed" ||
        normalizedDescription === "positive"
      );
    },

    is_negative_result() {
      // Result codes '3' (no record) or '9' (not verified) indicate negative
      return this.result_code === "3" || this.result_code === "9";
    },

    is_unconfirmed_result() {
      // Result code '2' indicates unconfirmed
      return this.result_code === "2";
    },

    result_state_class() {
      if (this.is_positive_result) {
        return "status-positive";
      }
      if (this.is_negative_result) {
        return "status-negative";
      }
      if (this.is_unconfirmed_result) {
        return "status-pending";
      }
      return "status-neutral";
    },

    verification_status_class() {
      if (this.verification_status === 'success') {
        return "text-success fw-bold";
      } else {
        return "text-danger fw-bold";
      }
    },

    result_status_class() {
      if (this.is_positive_result) {
        return "text-success fw-bold";
      } else if (this.is_negative_result) {
        return "text-danger fw-bold";
      } else if (this.is_unconfirmed_result) {
        return "text-warning fw-bold";
      } else {
        return "text-secondary fw-bold";
      }
    },

    result_summary_text() {
      const descriptionText = this.getResultCodeDescription(this.result_code);
      const baseDescription = this.result_description || "";

      if (!descriptionText) {
        return baseDescription || "Result details unavailable";
      }

      if (baseDescription && descriptionText.toLowerCase().includes(baseDescription.toLowerCase())) {
        return descriptionText;
      }

      return baseDescription ? `${baseDescription} - ${descriptionText}` : descriptionText;
    },

    verification_outcome_display() {
      if (this.is_positive_result) {
        return "Confirmed - Verified";
      } else if (this.is_unconfirmed_result) {
        return "Unconfirmed - Data mismatch";
      } else if (this.result_code === "3") {
        return "Not Found";
      } else if (this.result_code === "9") {
        return "Not Found";
      }
      return "Unknown";
    },

    verification_status_display() {
      if (this.is_positive_result) {
        return "Confirmed";
      } else if (this.is_unconfirmed_result) {
        return "Unconfirmed";
      } else if (this.result_code === "3" || this.result_code === "9") {
        return "Not Found";
      }
      return "Unknown";
    },

    result_display() {
      if (this.is_positive_result) {
        return "Verified";
      } else if (this.is_unconfirmed_result) {
        return "Information mismatch";
      } else if (this.result_code === "3" || this.result_code === "9") {
        return "N/A";
      }
      return "Unknown";
    },

    // Reasons for result
    reasons() {
      return this.response_data.reasons || [];
    },

    has_reasons() {
      return this.reasons.length > 0;
    },

    status_reason_text() {
      if (!this.is_unconfirmed_result || !this.has_reasons) {
        return "";
      }

      const firstReason = this.reasons[0];
      if (typeof firstReason === "string") {
        return firstReason;
      }

      if (firstReason && typeof firstReason === "object") {
        const headerParts = [];
        if (firstReason.code) {
          headerParts.push(`Code ${firstReason.code}`);
        }
        if (firstReason.title) {
          headerParts.push(firstReason.title);
        }

        let text = headerParts.join(" – ");
        if (firstReason.description) {
          text = text ? `${text}: ${firstReason.description}` : firstReason.description;
        }

        return text || "Additional verification steps required.";
      }

      return "Additional verification steps required.";
    },

    // Dates
    effect_date() {
      return this.response_data.effect_date || "N/A";
    },

    created_at() {
      return this.result_data.created_at || "N/A";
    },

    formatted_created_at() {
      return this.formatDate(this.created_at);
    },

    updated_at() {
      return this.result_data.updated_at || "N/A";
    },

    formatted_updated_at() {
      return this.formatDate(this.updated_at);
    },

    // Export functionality
    has_selected_tables() {
      return Object.values(this.export_options).some((option) => option);
    },

    report_reference() {
      return this.client_reference_number || `REF-${Date.now()}`;
    },

    generated_date() {
      return new Date().toLocaleString();
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString || dateString === "N/A") return "N/A";
      return format_date(dateString);
    },

    formatDateOfBirth(dob) {
      if (!dob || dob === "N/A") return "N/A";
      
      // Handle format: YYYYMMDD
      if (dob.length === 8) {
        const year = dob.substring(0, 4);
        const month = dob.substring(4, 6);
        const day = dob.substring(6, 8);
        return `${day}/${month}/${year}`;
      }
      
      return dob;
    },

    getQualificationDescription(code) {
      // Document type codes mapped to descriptions (from UMALUSI constants)
const qualifications = {
        "11": "Senior Certificate (G12)",
        "12": "Senior Certificate (G12) - Subject Statement",
        "21": "NSC (from 2008/11)",
        "22": "NSC (from 2008/11) - Subject Statement",
        "31": "NC (Vocational) Level 4 (NC(V))",
        "32": "NC (Vocational) Level 4 (NC(V)) - Subject Statement",
        "41": "Technical College NSC",
        "42": "Technical College NSC / N3 - Subject Statement",
        "51": "GETC (ABET Level 4)",
        "52": "GETC (ABET Level 4) - Learning Area Certificate",
        "61": "N3",
        "71": "NC (Vocational) Level 3",
        "72": "NC (Vocational) Level 3 - Subject Statement",
        "81": "NC (Vocational) Level 2",
        "82": "NC (Vocational) Level 2 - Subject Statement",
        "91": "Matric Yes/No (includes codes 11, 21, 31 or 41)",
      };
      return qualifications[code] || `Qualification Code: ${code}`;
    },

    getResultDescription(code) {
      // Result codes from UMALUSI
      const results = {
        "1": "Positive",
        "2": "Unconfirmed",
        "3": "No record found",
        "4": "Positive with reason 5",
        "9": "Not verified",
      };
      return results[code] || "Unknown";
    },

    getResultCodeDescription(code) {
      // Detailed result code descriptions from UMALUSI
      const descriptions = {
        "1": "Positive - Verification successful",
        "2": "Unconfirmed - Information supplied does not fully match the official record (possible data mismatch)",
        "3": "No record found - No matching record in database",
        "4": "Positive with reason 5 - Verified with specific conditions",
        "9": "Not verified - Verification could not be completed",
      };
      return descriptions[code] || "Status unknown";
    },

    getMarkBadgeClass(mark) {
      if (!mark || mark === 'N/A') return 'bg-secondary';
      const numMark = parseFloat(mark);
      if (numMark >= 80) return 'bg-success';
      if (numMark >= 60) return 'bg-info';
      if (numMark >= 50) return 'bg-warning text-dark';
      return 'bg-danger';
    },

    show_export_modal() {
      this.export_options.verification_data = true;
      this.export_options.response_data = true;
      this.export_options.subjects = true;
      this.export_options.raw_json = false;
      $("#export-modal-" + this.report_reference).modal("show");
    },

    close_modal() {
      $("#export-modal-" + this.report_reference).modal("hide");
    },

    async ensure_libraries_loaded() {
      try {
        if (this.export_format === "csv" && !this.libraries_loaded.papa_parse) {
          if (typeof Papa === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js"
            );
          }
          this.libraries_loaded.papa_parse = true;
        }

        if (this.export_format === "excel" && !this.libraries_loaded.sheetjs) {
          if (typeof XLSX === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"
            );
          }
          this.libraries_loaded.sheetjs = true;
        }
      } catch (error) {
        throw new Error("Failed to load export libraries");
      }
    },

    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async export_data() {
      try {
        await this.ensure_libraries_loaded();

        const export_data = this.prepare_export_data();

        if (this.export_format === "csv") {
          this.export_as_csv(export_data);
        } else if (this.export_format === "excel") {
          this.export_as_excel(export_data);
        } else if (this.export_format === "json") {
          this.export_as_json(export_data);
        }

        this.close_modal();
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },

    prepare_export_data() {
      const export_data = {};

      if (this.export_options.verification_data) {
        export_data.verification_data = [
          {
            names: this.names,
            surname: this.surname,
            maiden_name: this.maiden_name,
            id_number: this.id_number,
            date_of_birth: this.date_of_birth,
            year: this.year,
            qualification: this.qualification,
            qualification_description: this.qualification_description,
            document_number: this.document_number,
            aggregate: this.aggregate,
            number_of_subjects: this.number_of_subjects,
          },
        ];
      }

      if (this.export_options.response_data) {
        export_data.response_data = [
          {
            reference_number: this.reference_number,
            client_reference_number: this.client_reference_number,
            batch_number: this.batch_number,
            sequence_number: this.sequence_number,
            result_code: this.result_code,
            result_description: this.result_description,
            document_type: this.document_type,
            effect_date: this.effect_date,
            created_at: this.created_at,
            updated_at: this.updated_at,
          },
        ];
      }

      if (this.export_options.subjects && this.has_subjects) {
        export_data.subjects = this.subjects.map((subject, index) => ({
          subject_number: index + 1,
          subject_name: subject.name || "N/A",
          subject_code: subject.code || "N/A",
          mark: subject.mark || "N/A",
        }));
      }

      if (this.export_options.raw_json) {
        export_data.raw_json = this.safe_academic_data;
      }

      return export_data;
    },

    export_as_csv(export_data) {
      var Papa = window.Papa;
      Object.keys(export_data).forEach((table_name) => {
        const data = export_data[table_name];
        if (table_name === "raw_json") {
          const csv_content = Papa.unparse([data]);
          this.download_file(
            `academic_post92_${table_name}.csv`,
            csv_content
          );
        } else {
          const csv_content = Papa.unparse(data);
          this.download_file(
            `academic_post92_${table_name}.csv`,
            csv_content
          );
        }
      });
    },

    export_as_excel(export_data) {
      var XLSX = window.XLSX;
      const workbook = XLSX.utils.book_new();

      Object.keys(export_data).forEach((table_name) => {
        const data = export_data[table_name];
        let worksheet;

        if (table_name === "raw_json") {
          worksheet = XLSX.utils.json_to_sheet([data]);
        } else {
          worksheet = XLSX.utils.json_to_sheet(data);
        }

        XLSX.utils.book_append_sheet(workbook, worksheet, table_name);
      });

      XLSX.writeFile(workbook, "academic_post92_report.xlsx");
    },

    export_as_json(export_data) {
      const json_content = JSON.stringify(export_data, null, 2);
      this.download_file("academic_post92_report.json", json_content);
    },

    download_file(filename, content) {
      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    position_disclaimer() {
      this.$nextTick(() => {
        const academicCard = this.$refs.academicCard;
        const reasonsSection = this.$refs.reasonsSection;
        const disclaimerCard = this.$refs.disclaimerCard;

        if (!academicCard || !disclaimerCard || !academicCard.parentNode) {
          return;
        }

        if (this.is_unconfirmed_result && reasonsSection) {
          reasonsSection.insertAdjacentElement("afterend", disclaimerCard);
        } else {
          academicCard.insertAdjacentElement("afterend", disclaimerCard);
        }
      });
    },
  },
};
